import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { cssProperties } from "../utils/commonCssProperties";

const cellStyle = {
  overflow: "hidden",
  wordWrap: "break-word",
  whiteSpace: "nowrap",
  textAlign: "center",
};

const headerCellStyle = {
  ...cellStyle,
  whiteSpace: "nowrap",
};

export default function PreferenceWiseTable({ tableData, columnData }) {
  const [rows, setRows] = React.useState(tableData);

  React.useEffect(() => {
    setRows(tableData);
  }, [tableData]);

  const totalRow = React.useMemo(() => {
    if (!rows || rows.length === 0) return null;

    const totals = {};

    columnData.forEach((col) => {
      if (col.subHeaders) {
        col.subHeaders.forEach((sub) => {
          const key = sub.dataIndex;

          totals[key] = rows.reduce((sum, r) => sum + Number(r[key] || 0), 0);
        });
      }
    });

    return {
      preference: "TOTAL",
      city: "",
      ...totals,
    };
  }, [rows, columnData]);

  return (
    <div>
      <TableContainer
        component={Paper}
        sx={{
          background: "none",
          border: `1px solid ${cssProperties.bordercolor.primary2blue}`,
          boxShadow: "none",
        }}
      >
        <Table aria-label="application status table">
          {/* ================= HEADER ================= */}
          <TableHead>
            {/* ===== FIRST HEADER ROW ===== */}
            <TableRow>
              {columnData.map((col, index) => {
                if (col.isPlaceholder) {
                  return (
                    <TableCell
                      key={`placeholder-${index}`}
                      rowSpan={2}
                      sx={{
                        borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                        ...headerCellStyle,
                      }}
                    />
                  );
                }

                return (
                  <TableCell
                    key={index}
                    colSpan={col.colSpan || 1}
                    rowSpan={col.subHeaders ? 1 : 2}
                    sx={{
                      color: cssProperties.bordercolor.primary2blue,
                      borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                      ...headerCellStyle,
                    }}
                    align="center"
                  >
                    {col.name}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* ===== SECOND HEADER ROW ===== */}
            <TableRow>
              {columnData.map((col, colIndex) =>
                col.subHeaders
                  ? col.subHeaders.map((sub, subIndex) => (
                      <TableCell
                        key={`${colIndex}-${subIndex}`}
                        sx={{
                          color: cssProperties.bordercolor.primary2blue,
                          borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                          ...headerCellStyle,
                        }}
                        align="center"
                      >
                        {sub.name}
                      </TableCell>
                    ))
                  : null
              )}
            </TableRow>
          </TableHead>

          {/* ================= BODY ================= */}
          <TableBody>
            {rows.length > 0 ? (
              <>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columnData.map((col, colIndex) =>
                      col.subHeaders ? (
                        col.subHeaders.map((sub, subIndex) => (
                          <TableCell
                            key={`${rowIndex}-${colIndex}-${subIndex}`}
                            sx={{
                              borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                              ...cellStyle,
                            }}
                            align="center"
                          >
                            {row[sub.dataIndex] ?? "-"}
                          </TableCell>
                        ))
                      ) : (
                        <TableCell
                          key={`${rowIndex}-${colIndex}`}
                          sx={{
                            borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                            ...cellStyle,
                          }}
                          align="center"
                        >
                          {row[col.dataIndex] ?? ""}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}

                {/* ===== TOTAL ROW ===== */}
                {totalRow && (
                  <TableRow sx={{ backgroundColor: "#f5f8ff" }}>
                    {columnData.map((col, colIndex) =>
                      col.subHeaders
                        ? col.subHeaders.map((sub, subIndex) => (
                            <TableCell
                              key={`total-${colIndex}-${subIndex}`}
                              sx={{
                                borderRight: `1px solid ${cssProperties.bordercolor.primary2blue}`,
                                fontWeight: "bold",
                                ...cellStyle,
                              }}
                              align="center"
                            >
                              {sub.dataIndex === "preference"
                                ? "TOTAL"
                                : sub.dataIndex === "city"
                                ? ""
                                : Number.isFinite(totalRow[sub.dataIndex])
                                ? totalRow[sub.dataIndex]
                                : 0}
                            </TableCell>
                          ))
                        : null
                    )}
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={30}
                  sx={{
                    textAlign: "center",
                    color: cssProperties.bordercolor.primary2blue,
                  }}
                >
                  No Result Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
